import React, { useCallback, useEffect, useMemo } from "react";
import * as Scrivito from "scrivito";
import { camelCase } from "lodash-es";
import { handleProjectListFiltersUpdate, updateProjectFilters } from "utils";
import { useTranslate } from "hooks";
import { FilterBtn } from "./FilterBtn";

export const ProjectListFilters = Scrivito.connect(
  ({
    overallStatistics,
    projectsCount,
    setSearchQuery,
    setSharedStatusSwitcher,
    projectStatuses,
    setFilterStatuses,
    filters,
    setFilters,
    statistics,
    setIsSelectAll,
    setProjectsToDelete,
    setCurrentPageNo,
  }) => {
    const translate = useTranslate();
    const translations = useMemo(
      () => ({
        shared: translate("SHARED"),
        notShared: translate("NOT_SHARED"),
        allProjects: translate("ALL_PROJECTS"),
      }),
      [translate],
    );

    useEffect(() => {
      if (filters.length <= 1) {
        const newFilters = updateProjectFilters(
          translations,
          overallStatistics,
          projectsCount,
          setSearchQuery,
          setSharedStatusSwitcher,
          setFilters,
          projectStatuses,
        );
        setFilters(newFilters);
      }
    }, [
      filters.length,
      translations,
      overallStatistics,
      projectsCount,
      setSearchQuery,
      setSharedStatusSwitcher,
      setFilters,
      projectStatuses,
    ]);

    useEffect(() => {
      setFilterStatuses(
        filters?.reduce((newStatuses, filter) => {
          if (filter.isStatusFilter && filter.isActive) {
            newStatuses.push(filter.name);
          }
          return newStatuses;
        }, []),
      );
    }, [filters, setFilterStatuses]);

    useEffect(() => {
      if (!statistics && !overallStatistics) {
        return;
      }

      setFilters((prevFilters) =>
        prevFilters?.map((fl) => {
          let count = 0;
          const flCamelCase = camelCase(fl.id);

          if (flCamelCase === "allProjects") {
            count = projectsCount;
          }
          if (flCamelCase === "shared" || flCamelCase === "notShared") {
            count = overallStatistics[flCamelCase];
          }
          if (statistics?.[flCamelCase]) {
            count = statistics?.[flCamelCase];
          } else if (
            statistics?.statuses &&
            overallStatistics?.statuses?.[fl.id]
          ) {
            count = overallStatistics?.statuses?.[fl.id];
          }

          return {
            ...fl,
            count,
          };
        }),
      );
    }, [statistics, overallStatistics, projectsCount, setFilters]);

    const applyFilter = useCallback(
      (id) => {
        setFilters((prev) => handleProjectListFiltersUpdate(prev, { id }));
        setCurrentPageNo(1);
        setProjectsToDelete([]);
        setIsSelectAll(false);
      },
      [setFilters, setCurrentPageNo, setProjectsToDelete, setIsSelectAll],
    );

    const handleFilterActivation = useCallback(
      (id) => {
        applyFilter(id);

        Scrivito.navigateTo(() => Scrivito.currentPage(), {
          params: { page: "1" },
        });
      },
      [applyFilter],
    );

    return (
      <div className="d-flex flex-wrap gap-2 justify-content-end">
        {filters.map(({ id, name, count, color, icon, isActive, onClick }) => (
          <FilterBtn
            key={id}
            id={id}
            name={name}
            count={count || 0}
            color={color}
            icon={icon}
            isActive={isActive}
            onClick={onClick ?? handleFilterActivation}
          />
        ))}
      </div>
    );
  },
);
